import * as Sentry from '@sentry/react';
import { useMutation, useQuery } from '@tanstack/react-query';

import apiClient from '../axios';
import type { ScanRanges } from '../types/scan-ranges';

const getDeviceScanRanges = async (id: string): Promise<ScanRanges | null> => {
  try {
    const { data } = await apiClient.get<ScanRanges | null>(`/devices/${id}/scan-ranges`);
    return data;
  } catch (error) {
    Sentry.captureException(error);
    console.debug({ error });
    throw new Error('Failed to fetch device scan ranges');
  }
};

const updateDeviceScanRanges = async (params: {
  id: string;
  scanRanges: ScanRanges;
}): Promise<void> => {
  try {
    const { id, scanRanges } = params;
    const { data } = await apiClient.post(`/devices/${id}/scan-ranges`, scanRanges);
    return data;
  } catch (error) {
    Sentry.captureException(error);
    console.debug({ error });
    throw new Error('Failed to update device scan ranges');
  }
};

export const useDeviceScanRanges = (id: string | null | undefined, enabled?: boolean) => {
  return useQuery<ScanRanges | null>({
    queryKey: ['device', 'scan-ranges', id],
    queryFn: () => getDeviceScanRanges(id as string),
    enabled: !!id && (enabled ?? true),
    staleTime: 0,
  });
};

export const useUpdateDeviceScanRanges = () => {
  return useMutation({
    mutationFn: updateDeviceScanRanges,
  });
};
